import React, { useEffect, useRef, useState } from "react";
import { bool, node, object, func } from "prop-types";
import "!style-loader!css-loader!../styles.css";
import UpgradeBanner from "./UpgradeBanner";

const CustomDropdownMenu = ({
  children,
  collaboratorOnlyPermission,
  isFreeTrial,
  isTSS,
  showProUpgrades,
  userData,
  closeDropdown,
  show, // Add prop to track visibility of the dropdown
}) => {
  const isVideoInterviewsEnabled = userData?.videoInterviewsEnabled === 1;

  // Free Trials - bottom CTA will be to schedule a call that will open a Calendly page for the SE in a different tab
  // Subscribers without SI and/or on Essentials - bottom CTA will link to the “Compare Plans” page in the same tab
  // TSS Subscribers or Subscribers with SI and not on Essentials - no bottom CTA banner
  const showUpgradeBanner =
    !(
      (isTSS && !isFreeTrial) ||
      (!isFreeTrial && isVideoInterviewsEnabled && !showProUpgrades)
    ) && !collaboratorOnlyPermission;

  // Reference to the dropdown container
  const dropdownRef = useRef(null);
  // Reference to just the content container (without banner)
  const contentRef = useRef(null);
  // State to track if it's a single column layout
  const [isSingleColumn, setIsSingleColumn] = useState(false);
  // State to track if measurement is done
  const [measurementComplete, setMeasurementComplete] = useState(false);

  // Layout effect to measure dropdown width and handle responsive behavior
  // Effect to measure dropdown width after render, on resize, and when dropdown is shown
  useEffect(() => {
    // Function to check if dropdown width is 240px or less and adjust dropdown width
    const checkWidth = () => {
      if (contentRef.current) {
        // First, remove any inline styles that might be affecting measurement
        contentRef.current.style.width = "auto";

        // Wait for next frame to get accurate measurements after style changes
        requestAnimationFrame(() => {
          // Calculate the number of columns based on element heights
          const elements = Array.from(contentRef.current.children);
          if (elements.length === 0) {
            setMeasurementComplete(true);
            return;
          }

          // Get all category sections and their heights
          const categorySections = Array.from(
            contentRef.current.querySelectorAll(".category-section")
          );
          if (categorySections.length === 0) {
            setMeasurementComplete(true);
            return;
          }

          // Calculate total height of all sections
          let totalHeight = 0;
          categorySections.forEach((section) => {
            const { height } = section.getBoundingClientRect();
            totalHeight += height + 16; // Add gap
          });

          // Estimate number of columns needed
          // Set maxHeight based on responsive breakpoints defined in CSS
          let maxHeight = 320; // Default for most screen sizes
          const screenWidth = window.innerWidth;
          // Match the breakpoints from CSS media queries (768-950px and 574-766px)
          if (
            (screenWidth >= 768 && screenWidth <= 950) ||
            (screenWidth >= 574 && screenWidth <= 766)
          ) {
            maxHeight = 480;
          }

          let numColumns = Math.ceil(totalHeight / maxHeight);
          numColumns = Math.max(1, Math.min(numColumns, 3)); // Limit to 1-3 columns

          // Set single column state
          setIsSingleColumn(numColumns === 1);

          // Manually set width to force Firefox to respect the column layout
          if (navigator.userAgent.includes("Firefox")) {
            // Calculate width: only include column width and gap between columns
            const categoryWidth = 232;
            const gap = 16; // Gap between columns in px
            const leftRightPadding = 8; // Padding on each side from CSS

            // columns * width + gaps between columns
            const targetWidth =
              numColumns * categoryWidth +
              (numColumns - 1) * gap +
              leftRightPadding;

            contentRef.current.style.width = `${targetWidth}px`;
          }

          // After measuring, mark it as complete so banner can render
          setMeasurementComplete(true);
        });
      }
    };

    // Check width when the dropdown is shown
    if (show) {
      // Reset measurement complete when dropdown shows
      setMeasurementComplete(false);
      // Use setTimeout to ensure the DOM has updated
      setTimeout(checkWidth, 0);
    }

    // Add resize listener
    window.addEventListener("resize", checkWidth);
    return () => {
      window.removeEventListener("resize", checkWidth);
    };
  }, [show]); // Add show as a dependency to re-run when visibility changes

  return (
    <div
      ref={dropdownRef}
      className={`manage-nav-custom-dropdown ${isSingleColumn ? "single-column" : ""}`}
    >
      <div className="manage-menu-dropdown-content" ref={contentRef}>
        {children}
      </div>
      {/* Only show banner if measurement is complete, not collaborator, not TSS, and not single column */}
      {measurementComplete && showUpgradeBanner && !isSingleColumn && (
        <UpgradeBanner
          isFreeTrial={isFreeTrial}
          userData={userData}
          closeDropdown={closeDropdown}
        />
      )}
    </div>
  );
};

CustomDropdownMenu.propTypes = {
  children: node.isRequired,
  collaboratorOnlyPermission: bool,
  isFreeTrial: bool,
  isTSS: bool,
  showProUpgrades: bool,
  userData: object,
  closeDropdown: func,
  show: bool, // Add show prop validation
};

export default CustomDropdownMenu;
